import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calculator, Wallet, GraduationCap, FileText, Crown,
  ChevronRight, Menu, X, Lock, User, Home, BookOpen, HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/lib/api';
import { useTour } from '@/context/TourContext';

const QuickNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const { startTour } = useTour();

  // Hide on admin and auth pages
  const hiddenPaths = ['/admin', '/auth/callback'];
  const shouldHide = hiddenPaths.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscription(response.data);
    } catch (error) {
      setSubscription({ subscription_type: 'guest' });
    }
  };

  const isPremium = subscription?.subscription_type === 'premium';
  const isBasicOrHigher = ['basic', 'premium'].includes(subscription?.subscription_type);
  const isLoggedIn = subscription?.subscription_type !== 'guest';

  const navItems = [
    { id: 'home', label: 'Inicio', icon: Home, path: '/', available: true, description: 'Pagina inicial' },
    { id: 'profile', label: 'Meu Perfil', icon: User, path: '/dashboard', available: isLoggedIn, requiresLogin: true, description: 'Historico e conta' },
    { id: 'expenses', label: 'Organizador', icon: Wallet, path: '/expenses', available: true, description: 'Organizar despesas' },
    { id: 'analyze', label: 'Nova Analise', icon: FileText, path: '/analyze', available: true, description: 'Analisar decisao' },
    { id: 'blog', label: 'Blog', icon: BookOpen, path: '/blog', available: true, description: 'Dicas financeiras' },
    { id: 'simulator', label: 'Simulador', icon: Calculator, path: '/simulator', available: true, description: 'Simular investimentos' },
    { id: 'investments', label: 'Investimentos', icon: GraduationCap, path: '/investments', available: true, description: 'Educacao financeira' },
    { id: 'pricing', label: 'Planos', icon: Crown, path: '/pricing', available: true, highlight: !isPremium, description: 'Ver planos' },
  ];

  const currentPath = location.pathname;

  if (shouldHide) return null;

  return (
    <>
      {/* Hamburger Button - Fixed top-left */}
      <motion.button
        className="fixed left-4 top-5 z-[55] w-11 h-11 bg-[#0A0A0A]/90 backdrop-blur-md border border-white/15 rounded-xl flex items-center justify-center text-white hover:border-[#00FF94]/50 hover:text-[#00FF94] transition-all shadow-lg shadow-black/30"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="quick-nav-toggle"
        aria-label="Menu de navegacao"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <X className="w-5 h-5" />
            </motion.div>
          ) : (
            <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <Menu className="w-5 h-5" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Navigation Panel - Slides from LEFT */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/60 z-[54] backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />

            {/* Panel from LEFT */}
            <motion.div
              className="fixed left-0 top-0 h-full w-72 bg-[#0A0A0A] border-r border-white/10 z-[56] overflow-y-auto"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            >
              <div className="p-6 pt-20 pb-28">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-white">Menu</h2>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                    data-testid="quick-nav-close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Como mexer - Tour button (top, discreto) */}
                <button
                  className="w-full mb-5 px-3 py-2.5 rounded-xl flex items-center gap-3 bg-[#00FF94]/5 border border-[#00FF94]/20 hover:bg-[#00FF94]/15 transition-all"
                  onClick={() => {
                    setIsOpen(false);
                    startTour();
                  }}
                  data-testid="quick-nav-tour"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#00FF94]/15 flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-4 h-4 text-[#00FF94]" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-[#00FF94]">Como mexer</p>
                    <p className="text-xs text-gray-500">Tour interativo do site</p>
                  </div>
                </button>

                {/* User Plan Badge */}
                {isLoggedIn && (
                  <div className={`mb-6 p-3 rounded-xl ${
                    isPremium 
                      ? 'bg-amber-500/10 border border-amber-400/30' 
                      : isBasicOrHigher 
                        ? 'bg-[#00FF94]/10 border border-[#00FF94]/30'
                        : 'bg-white/5 border border-white/10'
                  }`}>
                    <div className="flex items-center gap-2">
                      <Crown className={`w-4 h-4 ${
                        isPremium ? 'text-amber-400' : isBasicOrHigher ? 'text-[#00FF94]' : 'text-gray-400'
                      }`} />
                      <span className={`text-sm font-medium ${
                        isPremium ? 'text-amber-400' : isBasicOrHigher ? 'text-[#00FF94]' : 'text-gray-400'
                      }`}>
                        Plano {isPremium ? 'Premium' : isBasicOrHigher ? 'Basico' : 'Gratuito'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Navigation Items */}
                <div className="space-y-2">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentPath === item.path;
                    const isLocked = !item.available;

                    return (
                      <motion.button
                        key={item.id}
                        className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all ${
                          isActive
                            ? 'bg-[#00FF94]/20 border border-[#00FF94]/30'
                            : isLocked
                              ? 'bg-white/5 border border-white/5 opacity-60'
                              : item.highlight
                                ? 'bg-amber-500/10 border border-amber-400/30 hover:bg-amber-500/20'
                                : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20'
                        }`}
                        onClick={() => {
                          if (isLocked) {
                            navigate('/pricing');
                          } else {
                            navigate(item.path);
                          }
                          setIsOpen(false);
                        }}
                        whileHover={{ scale: isLocked ? 1 : 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        data-testid={`quick-nav-${item.id}`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                          isActive ? 'bg-[#00FF94]/20' : item.highlight ? 'bg-amber-500/20' : 'bg-white/10'
                        }`}>
                          <Icon className={`w-4 h-4 ${
                            isActive ? 'text-[#00FF94]' : item.highlight ? 'text-amber-400' : isLocked ? 'text-gray-500' : 'text-white'
                          }`} />
                        </div>
                        <div className="flex-1 text-left">
                          <p className={`text-sm font-medium ${
                            isActive ? 'text-[#00FF94]' : item.highlight ? 'text-amber-400' : isLocked ? 'text-gray-500' : 'text-white'
                          }`}>
                            {item.label}
                          </p>
                          <p className="text-xs text-gray-500">{item.description}</p>
                        </div>
                        {isLocked ? (
                          <Lock className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className={`w-4 h-4 ${isActive ? 'text-[#00FF94]' : 'text-gray-500'}`} />
                        )}
                      </motion.button>
                    );
                  })}
                </div>

                {/* Upgrade CTA */}
                {!isPremium && isLoggedIn && (
                  <motion.div
                    className="mt-6 p-4 bg-gradient-to-br from-[#00FF94]/10 to-amber-500/10 rounded-xl border border-[#00FF94]/20"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <p className="text-sm text-white font-medium mb-2">Desbloqueie todos os recursos</p>
                    <p className="text-xs text-gray-400 mb-3">Analises ilimitadas + Organizador de Despesas</p>
                    <Button
                      className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76] text-sm font-bold"
                      onClick={() => { navigate('/pricing'); setIsOpen(false); }}
                    >
                      Fazer Upgrade
                    </Button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default QuickNav;
